import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export default function ReviewsSection({ reviews = [] }) {
  const { t, lang, L } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error(lang === "ar" ? "يرجى تعبئة الاسم والتقييم" : "Please fill name and review");
      return;
    }
    setSaving(true);
    try {
      await api.post("/reviews", form);
      toast.success(lang === "ar" ? "شكراً لكِ! سيظهر تقييمكِ بعد المراجعة" : "Thank you! Your review will appear after moderation");
      setOpen(false);
      setForm({ name: "", rating: 5, text: "" });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24" data-testid="reviews-section">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">{t.home.reviewsTitle}</h2>
        <p className="text-muted-foreground mt-3 text-lg">{t.home.reviewsSub}</p>
        <Button onClick={() => setOpen(true)} className="mt-6 rounded-full font-semibold" data-testid="add-review-cta">
          <Plus className="h-4 w-4 me-2" /> {lang === "ar" ? "أضيفي تقييمكِ" : "Add your review"}
        </Button>
      </div>

      {reviews.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-3">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-3xl bg-card cb-card-shadow p-7 relative"
              data-testid={`review-card-${r.id}`}
            >
              <Quote className="h-8 w-8 text-secondary absolute top-6 end-6 rtl-flip" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} className={`h-4 w-4 ${k < r.rating ? "fill-primary text-primary" : "text-muted"}`} />
                ))}
              </div>
              <p className="text-foreground/90 leading-relaxed">{L(r, "text")}</p>
              <p className="mt-5 font-semibold text-foreground">{L(r, "name")}</p>
            </motion.div>
          ))}
        </div>
      ) : (
        <p className="text-center text-muted-foreground">
          {lang === "ar" ? "كوني أول من يشاركنا تجربته الجميلة 💫" : "Be the first to share your experience"}
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" dir={lang === "ar" ? "rtl" : "ltr"}>
          <DialogHeader><DialogTitle>{lang === "ar" ? "شاركينا تقييمكِ" : "Share your review"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">{t.booking.fullName}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-12" data-testid="review-name-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">{lang === "ar" ? "التقييم" : "Rating"}</Label>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                    <Star className={`h-8 w-8 transition-colors ${n <= form.rating ? "fill-primary text-primary" : "text-muted-foreground/40"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">{lang === "ar" ? "تقييمكِ" : "Your review"}</Label>
              <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} className="rounded-xl" data-testid="review-text-input" />
            </div>
            <Button onClick={submit} disabled={saving} className="rounded-full w-full h-12 font-semibold" data-testid="review-submit-btn">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (lang === "ar" ? "إرسال التقييم" : "Submit review")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
